/**
 * RecipientAvatar Component
 *
 * Displays a recipient's avatar with initials and verification status badge.
 * The verification badge appears as an overlay in the bottom-right corner.
 */

import { Text, Icon, Tooltip } from '@shopify/polaris';
import { CheckIcon, ClockIcon } from '@shopify/polaris-icons';
import type { RecipientAvatarProps } from './types';
import { getInitials } from './utils';
import { AVATAR_SIZE } from './constants';

export function RecipientAvatar({ name, isVerified }: RecipientAvatarProps) {
  const initials = getInitials(name);

  return (
    <div className="recipient-avatar-container">
      {/* Main avatar circle with initials */}
      <div className={`recipient-avatar ${isVerified ? '' : 'recipient-avatar-unverified'}`}>
        <Text as="span" variant="bodySm" fontWeight="semibold">
          <span className="recipient-avatar-initials">{initials}</span>
        </Text>
      </div>

      {/* Verification status badge overlay */}
      <div
        className={`recipient-verification-badge ${
          isVerified
            ? 'recipient-verification-badge-verified'
            : 'recipient-verification-badge-pending'
        }`}
      >
        <Tooltip content={isVerified ? 'Email verified' : 'Verification pending'}>
          <span className="recipient-verification-badge-icon">
            <Icon
              source={isVerified ? CheckIcon : ClockIcon}
              tone={isVerified ? 'success' : 'caution'}
            />
          </span>
        </Tooltip>
      </div>
    </div>
  );
}
